import React, { useState, useEffect, useMemo, forwardRef, useImperativeHandle } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  FlatList,
  Keyboard,
} from "react-native";
import { useTranslation } from "react-i18next";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import Colors from "../../../../../constants/colors";
import { ComponentStyles } from "../../../../../constants/componentStyles";
import FloatingTextInputComponent from "../../../common/FloatingTextInputComponent";

type FieldInfo = {
  field: string;
  dataType: string;
  required?: boolean;
  fieldLabel?: string;
  displayOrder?: string;
  fieldMin?: string;
  fieldMax?: string;
  exampleText?: string;
  enumerationItem?: Array<{ value: string; description: string }>;
};

type Props = {
  onValidationChange?: (isValid: boolean) => void;
  testID?: string | undefined;
};

export interface MapiReceiverDetailsStepHandle {
  submit: () => Promise<boolean>;
  getValues: () => Record<string, string>;
}

//  THIS PAGE WILL DISPLAY ALL FIELDS THAT STARTS WITH receiver.name
//  AND REQUIRED IS TRUE

const OPTIONAL_NAME_FIELDS = [
  "receiver.name.middleName",
  "receiver.name.secondLastName",
];

const MapiReceiverDetailsStep = forwardRef<MapiReceiverDetailsStepHandle, Props>(
  ({ onValidationChange, testID }: Props, ref) => {
    const { t } = useTranslation();

    const mapiTransactionFieldsData = useSessionStore(
      (s: SessionState) => (s as any).mapiTransactionFieldsData
    );
    const fieldInfo = useMemo(
      () => (mapiTransactionFieldsData?.fieldInfo ?? []) as FieldInfo[],
      [mapiTransactionFieldsData]
    );

    const receiverNameFields = useMemo(() => {
      return fieldInfo
        .filter((f) => (f.field || "").startsWith("receiver.name"))
        .filter(
          (f) =>
            f.required === true || OPTIONAL_NAME_FIELDS.includes(f.field || "")
        )
        .sort((a, b) => {
          const orderA = parseInt(a.displayOrder || "999", 10);
          const orderB = parseInt(b.displayOrder || "999", 10);
          return orderA - orderB;
        });
    }, [fieldInfo]);

    const receiverAddressFields = useMemo(() => {
      return fieldInfo
        .filter((f) => (f.field || "").startsWith("receiver.address"))
        .filter((f) => f.required === true)
        .sort((a, b) => {
          const orderA = parseInt(a.displayOrder || "999", 10);
          const orderB = parseInt(b.displayOrder || "999", 10);
          return orderA - orderB;
        });
    }, [fieldInfo]);

    const allFields = useMemo(
      () => [...receiverNameFields, ...receiverAddressFields],
      [receiverNameFields, receiverAddressFields]
    );

    const requiredFields = useMemo(
      () => allFields.filter((f) => f.required === true),
      [allFields]
    );

    const [values, setValues] = useState<Record<string, string>>({});
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [dropdownOpen, setDropdownOpen] = useState<string | null>(null);

    const isValid = useMemo(() => {
      return requiredFields.every((f) => {
        const v = (values[f.field] || "").trim();
        return v.length > 0;
      });
    }, [requiredFields, values]);

    useEffect(() => {
      onValidationChange?.(isValid);
    }, [isValid, onValidationChange]);

    const updateValue = (field: string, value: string) => {
      setValues((prev) => ({ ...prev, [field]: value }));
      setErrors((prev) => ({ ...prev, [field]: "" }));
    };

    useImperativeHandle(ref, () => ({
      submit: async () => {
        const newErrors: Record<string, string> = {};
        requiredFields.forEach((f) => {
          const v = (values[f.field] || "").trim();
          if (!v) {
            newErrors[f.field] = `${f.fieldLabel || f.field} is required`;
          }
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
      },
      getValues: () => ({ ...values }),
    }));

    const getFieldLabel = (f: FieldInfo) => {
      const baseLabel = f.fieldLabel || f.field;
      const isOptional =
        !f.required || OPTIONAL_NAME_FIELDS.includes(f.field || "");
      return isOptional ? `${baseLabel} (Optional)` : baseLabel;
    };

    const renderField = (f: FieldInfo) => {
      const label = getFieldLabel(f);
      const value = values[f.field] || "";
      const error = errors[f.field] || "";
      const maxLen = f.fieldMax ? parseInt(f.fieldMax, 10) : undefined;
      const isOptional =
        !f.required || OPTIONAL_NAME_FIELDS.includes(f.field || "");

      if (
        f.dataType === "enum" &&
        Array.isArray(f.enumerationItem) &&
        f.enumerationItem.length > 0
      ) {
        const selectedItem = f.enumerationItem.find((e) => e.value === value);
        const displayValue = selectedItem?.description ?? "";

        return (
          <View key={f.field} style={styles.fieldWrapper}>
            <TouchableOpacity
              testID={`mapi-receiver-${f.field}`}
              style={[styles.dropdownTrigger, error ? styles.dropdownError : null]}
              onPress={() => {
                setDropdownOpen(dropdownOpen === f.field ? null : f.field);
                Keyboard.dismiss();
              }}
            >
              <Text
                style={[
                  styles.dropdownText,
                  !displayValue && styles.placeholderText,
                ]}
                numberOfLines={1}
              >
                {displayValue ||
                  (isOptional
                    ? `Select ${f.fieldLabel || f.field} (Optional)`
                    : `Select ${label}`)}
              </Text>
              <Text style={styles.dropdownArrow}>▼</Text>
            </TouchableOpacity>
            {dropdownOpen === f.field && (
              <View style={styles.dropdownContainer}>
                <FlatList
                  keyboardShouldPersistTaps="handled"
                  data={f.enumerationItem}
                  keyExtractor={(item) => item.value}
                  renderItem={({ item }) => (
                    <TouchableOpacity
                      testID={`mapi-receiver-option-${item.value}`}
                      style={styles.suggestionItem}
                      onPress={() => {
                        updateValue(f.field, item.value);
                        setDropdownOpen(null);
                      }}
                    >
                      <Text style={styles.suggestionText}>
                        {item.description}
                      </Text>
                    </TouchableOpacity>
                  )}
                />
              </View>
            )}
            {!!error && <Text style={styles.errorText}>{error}</Text>}
          </View>
        );
      }

      const keyboardType =
        f.dataType === "gYear" ||
        f.dataType === "gMonth" ||
        f.dataType === "gDay"
          ? "number-pad"
          : "default";

      return (
        <View key={f.field} style={styles.fieldWrapper}>
          <FloatingTextInputComponent
            testID={`mapi-receiver-${f.field}`}
            label={label}
            value={value}
            onChangeText={(v) => updateValue(f.field, v)}
            errorText={error}
            maxLength={maxLen}
            keyboardType={keyboardType as any}
          />
        </View>
      );
    };

    if (allFields.length === 0) {
      return (
        <View style={styles.container} testID={testID}>
          <Text style={styles.title}>
            {t("RECEIVER_DETAILS_TITLE") || "Receiver Details"}
          </Text>
          <Text style={styles.subtitle}>
            {t("RECEIVER_DETAILS_NO_FIELDS") ||
              "No receiver fields required for this transaction."}
          </Text>
        </View>
      );
    }

    return (
      <View style={styles.container} testID={testID}>
        <Text style={styles.title}>
          {t("RECEIVER_DETAILS_TITLE") || "Receiver Information"}
        </Text>
        <Text style={styles.subtitle}>
          {t("RECEIVER_DETAILS_SUBTITLE") ||
            "Please enter the receiver's name and address details."}
        </Text>

        {receiverNameFields.length > 0 && (
          <Text style={styles.sectionLabel}>
            {t("RECEIVER_NAME_SECTION") || "Receiver Name"}
          </Text>
        )}
        {receiverNameFields.map((f) => renderField(f))}

        {receiverAddressFields.length > 0 && (
          <Text style={[styles.sectionLabel, { marginTop: 16 }]}>
            {t("RECEIVER_ADDRESS_SECTION") || "Receiver Address"}
          </Text>
        )}
        {receiverAddressFields.map((f) => renderField(f))}
      </View>
    );
  }
);

export default MapiReceiverDetailsStep;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.white,
    paddingHorizontal: 24,
  },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: "500",
    color: Colors.titleTextColor,
    lineHeight: 34,
    marginBottom: 8,
  },
  subtitle: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: "400",
    lineHeight: 24,
    color: Colors.subTitleColor,
    marginBottom: 24,
  },
  sectionLabel: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: 14,
    fontWeight: "600",
    color: Colors.titleTextColor,
    marginBottom: 12,
  },
  fieldWrapper: {
    marginBottom: 16,
  },
  dropdownTrigger: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: Colors.textInputBackground,
    borderRadius: 8,
    paddingVertical: 14,
    paddingHorizontal: 12,
    minHeight: 56,
    borderWidth: 1,
    borderColor: Colors.borderGrey,
  },
  dropdownError: {
    borderColor: Colors.errorRedColor,
    borderWidth: 1,
  },
  dropdownText: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: 16,
    fontWeight: "400",
    color: Colors.titleTextColor,
    flex: 1,
  },
  placeholderText: {
    color: Colors.subTitleColor,
  },
  dropdownArrow: {
    fontSize: 10,
    color: Colors.subTitleColor,
    marginLeft: 8,
  },
  dropdownContainer: {
    maxHeight: 200,
    borderWidth: 1,
    borderColor: Colors.borderGrey,
    borderRadius: 8,
    backgroundColor: Colors.white,
    marginTop: 4,
  },
  suggestionItem: {
    padding: 12,
    borderBottomWidth: 1,
    borderColor: Colors.borderGrey,
    backgroundColor: Colors.white,
  },
  suggestionText: {
    fontSize: 16,
    fontFamily: ComponentStyles.textFamily,
    fontWeight: "400",
    color: Colors.titleTextColor,
    lineHeight: 22,
  },
  errorText: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: 12,
    color: Colors.errorRedColor,
    marginTop: 4,
  },
});
